// src/components/admin/VideoPreviewPlayer.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VideocamIcon from '@mui/icons-material/Videocam';
import COLORS from '../../utils/color';

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '200px',
  height: '120px',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: COLORS.background.secondary,
  border: `1px solid ${COLORS.border.primary}`,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: COLORS.primary.main,
    transform: 'scale(1.02)',
    '& .video-controls': {
      opacity: 1,
    },
  },
}));

const VideoElement = styled('video')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const VideoOverlay = styled(Box)(({ theme, isVisible }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.8) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: isVisible ? 1 : 0,
  transition: 'opacity 0.3s ease',
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  background: `linear-gradient(135deg, ${COLORS.primary.main}, ${COLORS.primary.light})`,
  color: 'white',
  width: '48px',
  height: '48px',
  boxShadow: `0 4px 15px ${COLORS.primary.main}40`,
  '&:hover': {
    background: `linear-gradient(135deg, ${COLORS.primary.light}, ${COLORS.primary.main})`,
    transform: 'scale(1.1)',
  },
}));

const VideoControls = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const ControlButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  padding: '4px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}));

const ProgressBarContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  margin: theme.spacing(0, 0.5),
}));

const VideoInfo = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  left: theme.spacing(1),
  right: theme.spacing(1),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
}));

const VideoBadge = styled(Box)(({ theme }) => ({
  background: 'rgba(0, 0, 0, 0.7)',
  borderRadius: '6px',
  padding: '2px 6px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

const ErrorState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: COLORS.text.secondary,
  background: COLORS.background.secondary,
}));

const VideoPreviewPlayer = ({ 
  videoUrl, 
  filename, 
  duration, 
  onFullscreenView,
  autoPlay = false,
  showControls = true 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(duration || 0);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setLoading(false);
      setError(false);
      setVideoDuration(video.duration);
    };

    const handleError = () => {
      setLoading(false);
      setError(true);
    };

    const handleTimeUpdate = () => {
      if (video.duration) {
        const progressPercent = (video.currentTime / video.duration) * 100;
        setProgress(progressPercent);
        setCurrentTime(video.currentTime);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, []);

  const handlePlayPause = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(console.error);
    }
  };

  const handleMuteToggle = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleFullscreen = (e) => {
    e.stopPropagation();
    if (onFullscreenView) {
      onFullscreenView();
    }
  };

  const handleContainerClick = () => {
    if (onFullscreenView) {
      onFullscreenView();
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!videoUrl) {
    return (
      <VideoContainer>
        <ErrorState>
          <VideocamIcon sx={{ fontSize: 32, mb: 1 }} />
          <Typography variant="caption" sx={{ textAlign: 'center' }}>
            No video available
          </Typography>
        </ErrorState>
      </VideoContainer>
    );
  }

  if (error) {
    return (
      <VideoContainer>
        <ErrorState>
          <VideocamIcon sx={{ fontSize: 32, mb: 1, color: COLORS.status.error }} />
          <Typography variant="caption" sx={{ textAlign: 'center', color: COLORS.status.error }}>
            Failed to load video
          </Typography>
        </ErrorState>
      </VideoContainer>
    );
  }

  return (
    <VideoContainer onClick={handleContainerClick}>
      <VideoElement
        ref={videoRef}
        src={videoUrl}
        muted={isMuted}
        preload="metadata"
        playsInline
      />

      {/* Video Info Overlay */}
      <VideoInfo>
        <VideoBadge>
          <VideocamIcon sx={{ fontSize: 12, color: 'white' }} />
          <Typography variant="caption" sx={{ color: 'white', fontSize: '10px' }}>
            {formatTime(videoDuration)}
          </Typography>
        </VideoBadge>
        
        <Tooltip title="Click to review">
          <VideoBadge>
            <FullscreenIcon sx={{ fontSize: 12, color: 'white' }} />
          </VideoBadge>
        </Tooltip>
      </VideoInfo>

      {/* Play Button Overlay */}
      <VideoOverlay isVisible={!isPlaying || loading}>
        {loading ? (
          <Box sx={{ color: 'white' }}>
            <Typography variant="caption">Loading...</Typography>
          </Box>
        ) : (
          <PlayButton onClick={handlePlayPause}>
            <PlayArrowIcon sx={{ fontSize: 24 }} />
          </PlayButton>
        )}
      </VideoOverlay>

      {/* Video Controls */}
      {showControls && (
        <VideoControls className="video-controls">
          <ControlButton onClick={handlePlayPause}>
            {isPlaying ? <PauseIcon sx={{ fontSize: 16 }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
          </ControlButton>

          <ProgressBarContainer>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: COLORS.primary.main,
                },
              }}
            />
          </ProgressBarContainer>

          <ControlButton onClick={handleMuteToggle}>
            {isMuted ? <VolumeOffIcon sx={{ fontSize: 16 }} /> : <VolumeUpIcon sx={{ fontSize: 16 }} />}
          </ControlButton>

          <ControlButton onClick={handleFullscreen}>
            <FullscreenIcon sx={{ fontSize: 16 }} />
          </ControlButton>
        </VideoControls>
      )}
    </VideoContainer>
  );
};

export default VideoPreviewPlayer;